import { z } from 'zod';

/**
 * @module @repo/shared-types/identity
 * Single Source of Truth für das zentrale Identitäts-/Autorisierungsmodell
 * (`identity`-Schema im Supabase-Projekt `gmolike-id`). Login-App, Admin-App,
 * SDK und Edge Functions leiten ihre Typen ausschließlich von hier ab.
 *
 * Siehe `docs/decisions/ADR-0004` (RBAC + Per-App-Grants) und
 * `docs/decisions/ADR-0005` (themebare Login-App / brand_tokens).
 */

/** Status einer App-Freischaltung pro Nutzer (Approval-Workflow). */
export const GrantStatusSchema = z.enum(['pending', 'approved', 'revoked']);
export type GrantStatus = z.infer<typeof GrantStatusSchema>;

/** Art eines Redirect-Ziels: Web-Callback oder mobiler Deeplink (z. B. ue5). */
export const RedirectKindSchema = z.enum(['web', 'deeplink']);
export type RedirectKind = z.infer<typeof RedirectKindSchema>;

/**
 * Datengetriebenes Branding einer App (Spalte `apps.brand_tokens`, jsonb).
 * Die themebare Login-App rendert daraus ihr Look & Feel pro `?app=<slug>`,
 * sodass z. B. Flammenreiter sein Design behält, ohne eigene Login-Seite.
 * Alle Farbwerte als `oklch(...)`-Strings (perzeptuell uniform, CSS-Variablen).
 */
export const BrandTokensSchema = z.object({
  /** Anzeigename im Login-Header. */
  displayName: z.string().min(1),
  /** Bevorzugtes Farbschema beim ersten Paint (No-FOUC). */
  mode: z.enum(['dark', 'light']).default('dark'),
  fonts: z
    .object({
      heading: z.string(),
      body: z.string(),
      decorative: z.string().optional(),
    })
    .partial({ decorative: true }),
  /** oklch-Palette; Schlüssel = CSS-Custom-Property-Namen ohne Präfix. */
  palette: z.object({
    bg: z.string(),
    surface: z.string(),
    fg: z.string(),
    muted: z.string(),
    primary: z.string(),
    primaryFg: z.string(),
    accent: z.string(),
    border: z.string(),
  }),
  /** Optionaler vollflächiger Hero-Hintergrund (MP4-Loop) + Poster-Fallback. */
  hero: z
    .object({
      video: z.string().url().optional(),
      poster: z.string().url().optional(),
      image: z.string().url().optional(),
    })
    .optional(),
  /** In-world-Copy; überschreibt Default-Texte der Login-App. */
  copy: z
    .object({
      title: z.string().optional(),
      subtitle: z.string().optional(),
      submit: z.string().optional(),
      forgotPassword: z.string().optional(),
      rememberMe: z.string().optional(),
      footer: z.string().optional(),
    })
    .optional(),
});
export type BrandTokens = z.infer<typeof BrandTokensSchema>;

/**
 * Kanonisches Format einer App-Ziel-URL (`identity.apps.url`) — die EINE Regel,
 * die Client und Datenbank teilen.
 *
 * Exakt dieses Literal steht auch in der DB-Constraint `apps_url_scheme_check`
 * (`supabase/migrations/0014_app_url_and_canu_app.sql`). Der Golden-Test in
 * `identity.test.ts` liest die Migration und vergleicht beide Fundstellen
 * Zeichen für Zeichen — Drift zwischen Kontrakt und DB fällt damit sofort auf.
 *
 * Verwendet ausschließlich Syntax, die PostgreSQL-ARE und JavaScript identisch
 * auslegen: klein geschriebenes Schema `http`/`https`, danach ein nicht leerer
 * Host ohne Leerraum, optional Pfad/Query/Fragment ohne Leerraum, bis zum
 * Zeichenkettenende verankert.
 *
 * Bewusst NICHT `z.string().url()`: dessen WHATWG-Parser lässt jedes Schema
 * durch — `javascript:alert(1)`, `data:…`, `file://…`, `ftp://…` — und trimmt
 * umgebenden Leerraum stillschweigend weg. Der Wert wird in der App-Kachel als
 * `href` gerendert; ein durchgelassenes `javascript:` wäre dort eine XSS-Lücke.
 * Umgekehrt akzeptiert die DB-Regex ohne Host-Pflicht ein `https://`, an dem
 * jeder Leser stirbt: `z.array(AppSchema).parse` wirft für die GANZE Liste, eine
 * einzige kaputte Zeile leert also die komplette Übersicht.
 */
export const APP_URL_PATTERN = '^https?://[^\\s/?#]+([/?#][^\\s]*)?$';

/** Kompilierte Fassung von {@link APP_URL_PATTERN} (einmal je Prozess). */
const APP_URL_RE = new RegExp(APP_URL_PATTERN);

/** Eine registrierte App, die den zentralen Login nutzt. */
export const AppSchema = z.object({
  id: z.string().uuid(),
  /** Eindeutiger technischer Schlüssel, z. B. `flammenreiter-lore`. */
  slug: z
    .string()
    .min(2)
    .regex(/^[a-z0-9-]+$/, 'slug: nur a-z, 0-9 und Bindestrich'),
  name: z.string().min(1),
  active: z.boolean().default(true),
  brandTokens: BrandTokensSchema,
  /**
   * Öffentliche Basis-URL der App; Ziel der Kachel in der App-Übersicht (`/apps`).
   * `null` heißt „kein Ziel hinterlegt" — die DB-Spalte ist `null`-bar, weil noch
   * nicht jede App eine Startseite hat.
   *
   * Bewusst `.nullable()`, aber bewusst NICHT `.optional()`: jeder Konsument
   * dieses Schemas führt `url` in seiner PostgREST-Spaltenliste. Ein `undefined`
   * kann daher nur bedeuten, dass eine Spaltenliste die Spalte verloren hat — und
   * genau das muss laut scheitern. Mit `.optional()` käme der App-Editor auf
   * `url: undefined`, der Speichern-Pfad machte daraus `null` und löschte den
   * gepflegten Wert beim nächsten Speichern still aus der Datenbank.
   */
  url: z
    .string()
    .regex(
      APP_URL_RE,
      'url: nur vollständige http(s)-URLs mit Host und ohne Leerraum, z. B. https://kanu.gmolike.me',
    )
    .nullable(),
  /** Rolle, die self-service-Anfragen automatisch erhalten (optional). */
  defaultRole: z.string().nullable(),
  /** Darf ein Nutzer selbst Zugang anfragen (Status `pending`)? */
  selfService: z.boolean().default(false),
  createdAt: z.coerce.date(),
});
export type App = z.infer<typeof AppSchema>;

/** Erlaubtes Redirect-Ziel einer App (SSOT/Doku; GoTrue-Allowlist separat). */
export const AppRedirectSchema = z.object({
  id: z.string().uuid(),
  appId: z.string().uuid(),
  url: z.string().min(1),
  kind: RedirectKindSchema.default('web'),
});
export type AppRedirect = z.infer<typeof AppRedirectSchema>;

/** Rolle — global (`appId === null`) oder app-spezifisch. */
export const RoleSchema = z.object({
  id: z.string().uuid(),
  slug: z
    .string()
    .min(2)
    .regex(/^[a-z0-9-]+$/),
  name: z.string().min(1),
  appId: z.string().uuid().nullable(),
});
export type Role = z.infer<typeof RoleSchema>;

/** Atomare Berechtigung, z. B. `lore.edit`, `campaign.manage`. */
export const PermissionSchema = z.object({
  id: z.string().uuid(),
  slug: z
    .string()
    .min(2)
    .regex(/^[a-z0-9_.-]+$/),
});
export type Permission = z.infer<typeof PermissionSchema>;

/** Freischaltung eines Nutzers für eine App mit einer Rolle + Approval-Status. */
export const UserAppAccessSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  appId: z.string().uuid(),
  roleId: z.string().uuid(),
  status: GrantStatusSchema,
  grantedBy: z.string().uuid().nullable(),
  grantedAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
});
export type UserAppAccess = z.infer<typeof UserAppAccessSchema>;

/**
 * Internes, app-übergreifend nutzbares Nutzerprofil (`identity.user_profiles`).
 * Verknüpft die `auth.users`-UUID mit einem stabilen, menschenlesbaren Username,
 * den der Plattform-Admin bei der Freigabe vergibt. Andere Apps lesen den Username
 * aus dem JWT-Claim (`username`, via `custom_access_token_hook`) oder dieser Tabelle.
 */
export const UserProfileSchema = z.object({
  userId: z.string().uuid(),
  /** Stabiler, eindeutiger interner Username (klein, app-übergreifend referenzierbar). */
  username: z
    .string()
    .min(2)
    .max(32)
    .regex(/^[a-z0-9_]+$/, 'username: nur a-z, 0-9 und _'),
  /** Optionaler Anzeigename (frei, darf Leerzeichen/Großschreibung enthalten). */
  displayName: z.string().nullable().default(null),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type UserProfile = z.infer<typeof UserProfileSchema>;

/**
 * Vom Admin-Worker aufgelöster Nutzer — kombiniert `auth.users` (Email/Provider)
 * mit `identity.user_profiles` (Username). NUR über den service-role-gestützten
 * Admin-Worker (`identity-admin-api`) verfügbar, niemals direkt aus der SPA.
 */
export const AdminUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email().nullable(),
  /** Primärer Login-Provider, z. B. `email` | `google`. */
  provider: z.string().nullable(),
  /** Alle verknüpften Provider (z. B. `['google','email']`). */
  providers: z.array(z.string()).default([]),
  username: z.string().nullable(),
  displayName: z.string().nullable(),
  createdAt: z.coerce.date().nullable(),
});
export type AdminUser = z.infer<typeof AdminUserSchema>;

/** Eingabe zum Setzen/Ändern des internen Usernames eines Nutzers (Admin-Worker). */
export const SetUsernameRequestSchema = z.object({
  username: UserProfileSchema.shape.username,
  displayName: z.string().trim().min(1).max(64).optional(),
});
export type SetUsernameRequest = z.infer<typeof SetUsernameRequestSchema>;

/** Eintrag im Admin-Audit-Trail. */
export const AuditLogSchema = z.object({
  id: z.number().int(),
  actor: z.string().uuid().nullable(),
  action: z.string().min(1),
  target: z.record(z.string(), z.unknown()),
  createdAt: z.coerce.date(),
});
export type AuditLog = z.infer<typeof AuditLogSchema>;

/**
 * Kompakte `app_access`-Map aus dem JWT-Claim: `{ "<app-slug>": "<role-slug>" }`.
 * Wird vom `custom_access_token_hook` nur für `approved` Grants gesetzt
 * (bewusst klein gehalten — keine Permission-Listen, 4-KB-Cookie-Grenze).
 */
export const AppAccessMapSchema = z.record(z.string(), z.string());
export type AppAccessMap = z.infer<typeof AppAccessMapSchema>;

/** Relevante Claims des zentralen Supabase-Access-Tokens. */
export const JwtClaimsSchema = z.object({
  // Defensiv: der Claims-Reader dient nur dem Client-UX-Gating (echte Durchsetzung
  // via RLS). Ein striktes uuid() würde bei untypischem `sub` den gesamten
  // Claim-Read scheitern lassen → Nutzer wirkt fälschlich ausgeloggt.
  sub: z.string(),
  email: z.string().email().optional(),
  role: z.string().optional(),
  app_access: AppAccessMapSchema.default({}),
  /** Interner, app-übergreifender Username (aus `identity.user_profiles`, via Hook). */
  username: z.string().optional(),
  exp: z.number().optional(),
  iat: z.number().optional(),
});
export type JwtClaims = z.infer<typeof JwtClaimsSchema>;

/** Eingabe der Login-App: gewünschte App + Rücksprung-Ziel. */
export const LoginRequestSchema = z.object({
  app: z.string().min(2),
  redirect: z.string().url(),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;
